import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguageStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL || '';

const toSafeStr = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  const localized = getLocalized(val, lang);
  if (typeof localized === 'object' && localized !== null) {
    const firstStr = Object.values(localized).find((v) => typeof v === 'string');
    return firstStr || '';
  }
  return String(localized || '');
};

const extractImgUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object') return img.url || img.secure_url || img.src || '';
  return '';
};

export const ProjectsPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const portfolioLabels = {
    en: 'Our Portfolio', ru: 'Наши проекты', nl: 'Onze Portfolio', ro: 'Portofoliul Nostru'
  };

  const categoryLabels = {
    all: { en: 'All', ru: 'Все', nl: 'Alle', ro: 'Toate' },
    residential: { en: 'Residential', ru: 'Жилые', nl: 'Residentieel', ro: 'Rezidențial' },
    commercial: { en: 'Commercial', ru: 'Коммерческие', nl: 'Commercieel', ro: 'Comercial' },
    retail: { en: 'Retail', ru: 'Ритейл', nl: 'Retail', ro: 'Retail' }
  };

  const categories = [
    { id: 'all', label: categoryLabels.all[language] || 'All' },
    { id: 'residential', label: categoryLabels.residential[language] || 'Residential' },
    { id: 'commercial', label: categoryLabels.commercial[language] || 'Commercial' },
    { id: 'retail', label: categoryLabels.retail[language] || 'Retail' },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const url = filter === 'all' 
          ? `${API}/api/projects` 
          : `${API}/api/projects?category=${filter}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setProjects(Array.isArray(data) ? data : (data?.projects || []));
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [filter]);

  return (
    <div className="min-h-screen select-none" data-testid="projects-page">
      <div className="bg-surface section-padding">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-caption text-muted-foreground mb-4">{toSafeStr(t?.nav?.projects, 'Projects')}</p>
            <h1 className="text-h1 mb-8">
              {portfolioLabels[language] || 'Our Portfolio'}
            </h1>
            
            <div className="flex gap-4 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFilter(cat.id)}
                  className={`text-sm px-4 py-2 transition-colors ${
                    filter === cat.id
                      ? 'bg-foreground text-background'
                      : 'bg-transparent hover:bg-muted'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-muted mb-4" />
                <div className="h-4 bg-muted w-1/4 mb-2" />
                <div className="h-6 bg-muted w-3/4" />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">{toSafeStr(t?.common?.noResults, 'No projects found.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => {
              const projTitle = toSafeStr(getLocalized(project.title, language), language);
              const mainImg = extractImgUrl(project.images?.[0]);

              return (
                <motion.div
                  key={project.id || project._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Link
                    to={`/${language}/projects/${project.slug || project.id}`}
                    className="group block"
                    data-testid={`project-${project.slug || index}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-muted mb-4">
                      {mainImg && (
                        <img
                          src={mainImg}
                          alt={projTitle}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none"
                          draggable={false}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {toSafeStr(project.location, language)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {toSafeStr(project.year, language)}
                      </span>
                    </div>
                    <h3 className="text-xl font-medium group-hover:underline underline-offset-4">
                      {projTitle}
                    </h3>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const ProjectDetailPage = () => {
  const { slug } = useParams();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  // Переменные для обработки свайпов
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 40;

  const notFoundLabels = {
    en: 'Project not found', ru: 'Проект не найден', nl: 'Project niet найден', ro: 'Proiectul nu a fost găsit'
  };

  const galleryLabels = {
    en: 'Gallery', ru: 'Галерея', nl: 'Galerij', ro: 'Galerie'
  };

  const categoryLabels = {
    all: { en: 'All', ru: 'Все', nl: 'Alle', ro: 'Toate' },
    residential: { en: 'Residential', ru: 'Жилые', nl: 'Residentieel', ro: 'Rezidențial' },
    commercial: { en: 'Commercial', ru: 'Коммерческие', nl: 'Commercieel', ro: 'Comercial' },
    retail: { en: 'Retail', ru: 'Ритейл', nl: 'Retail', ro: 'Retail' }
  };

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/projects/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setProject(data);
        }
      } catch (error) {
        console.error('Failed to fetch project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="animate-pulse">
          <div className="h-[60vh] bg-muted" />
          <div className="container-custom py-12">
            <div className="h-8 bg-muted w-1/4 mb-4" />
            <div className="h-12 bg-muted w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center select-none">
        <div className="text-center">
          <h1 className="text-h2 mb-4">
            {notFoundLabels[language] || 'Project not found'}
          </h1>
          <Link to={`/${language}/projects`}>
            <Button variant="outline">
              ← {toSafeStr(t?.common?.backToProjects, 'Back to Projects')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const titleStr = toSafeStr(getLocalized(project.title, language), language);
  const descStr = toSafeStr(getLocalized(project.description, language), language);
  const normalizedImages = Array.isArray(project.images) ? project.images.map(extractImgUrl).filter(Boolean) : [];

  // Переключение изображений
  const handleNext = () => {
    if (normalizedImages.length <= 1) return;
    setActiveImage((prev) => (prev + 1) % normalizedImages.length);
  };

  const handlePrev = () => {
    if (normalizedImages.length <= 1) return;
    setActiveImage((prev) => (prev - 1 + normalizedImages.length) % normalizedImages.length);
  };

  // Жесты касания (Swipe)
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      handleNext(); // Свайп влево -> следующее фото
    } else if (distance < -minSwipeDistance) {
      handlePrev(); // Свайп вправо -> предыдущее фото
    }
  };

  return (
    <div className="min-h-screen select-none" data-testid="project-detail-page">
      <SEO
        title={titleStr}
        description={descStr}
        path={`projects/${project.slug || slug}`}
        image={normalizedImages[0]}
      />

      {/* Главный слайдер проекта */}
      <div
        className="relative h-[60vh] bg-neutral-100 overflow-hidden select-none cursor-pointer"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {normalizedImages[activeImage] && (
          <img
            src={normalizedImages[activeImage]}
            alt={titleStr}
            className="w-full h-full object-cover pointer-events-none transition-all duration-500 ease-out"
            draggable={false}
          />
        )}

        {/* Клик-зоны на компьютере и планшетах */}
        {normalizedImages.length > 1 && (
          <>
            <div
              onClick={handlePrev}
              className="absolute left-0 top-0 w-1/2 h-full z-10"
              title="Previous image"
            />
            <div
              onClick={handleNext}
              className="absolute right-0 top-0 w-1/2 h-full z-10"
              title="Next image"
            />
          </>
        )}

        {/* Градиентный срез снизу */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

        {/* Индикаторы-квадратики */}
        {normalizedImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="project-image-dots">
            {normalizedImages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.stopPropagation(); // Не дает сработать клику по фону
                  setActiveImage(i);
                }}
                className={`h-1.5 transition-all ${i === activeImage ? 'w-8 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
                aria-label={`Show image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="container-custom py-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span>{toSafeStr(project.location, language)}</span>
            <span>•</span>
            <span>{toSafeStr(project.year, language)}</span>
            <span>•</span>
            <span className="coordinate-project capitalize">
              {categoryLabels[project.category]?.[language] || toSafeStr(project.category, language)}
            </span>
          </div>

          <h1 className="text-h1 mb-8" data-testid="project-title">
            {titleStr}
          </h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-body-lg">{descStr}</p>
          </div>
        </div>

        {normalizedImages.length > 1 && (
          <div className="mt-16" data-testid="project-gallery">
            <p className="text-caption text-muted-foreground mb-6">
              {galleryLabels[language] || 'Gallery'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {normalizedImages.slice(1).map((img, index) => (
                <button
                  type="button"
                  key={`${img}-${index}`}
                  onClick={() => { setActiveImage(index + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="aspect-[4/3] overflow-hidden bg-muted group block"
                  data-testid={`project-gallery-img-${index}`}
                >
                  <img
                    src={img}
                    alt={`${titleStr} - ${index + 2}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16">
          <Link to={`/${language}/projects`}>
            <Button variant="outline">
              ← {toSafeStr(t?.common?.backToProjects, 'Back to Projects')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;
